import i18n from "i18next";
import { toast } from "sonner";
import { logWarning } from "web_myplann/Infrastructure/sentry";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import type { NavigationPayload } from "web_myplann/Utils/WebViewHandlers/Types/NotificationNavigationTypes";
import {
	type NavigationDestination,
	NavigationDestinationType,
	normalizeNavigationDestination,
} from "web_myplann/Utils/WebViewModel";

const NO_DEEP_LINK_GROUPS = new Set(["newevent", "portfolio", "silent", "old"]);

export const isNoDeepLinkGroup = (type: string | undefined): boolean =>
	!!type && NO_DEEP_LINK_GROUPS.has(type.trim().toLowerCase());

const DESTINATION_ROUTES: Partial<
	Record<NavigationDestination, (sourceId: string) => string>
> = {
	activity: AppRouting.MyPlann.ActivityDetail.path,
	activityTerm: AppRouting.MyPlann.ActivityDetail.path,
	activityTermNotice: AppRouting.MyPlann.ActivityDetail.path,
	task: AppRouting.MyPlann.TaskDetail.path,
	taskTerm: AppRouting.MyPlann.TaskDetail.path,
	taskTermNotice: AppRouting.MyPlann.TaskDetail.path,
	chat: AppRouting.MyPlann.Chat.Detail.path,
};

// Hodnoty z FCM pushe nemají zaručené casing ("Reminder", "ReminderTerm"),
// proto porovnáváme case-insensitive. `notificationType` je typ z BE,
// `type` je hodnota `group` přeposlaná nativní vrstvou.
export const isReminderDestination = (
	type: string | undefined,
	notificationType: string | undefined,
): boolean =>
	normalizeNavigationDestination(type) === NavigationDestinationType.reminder ||
	notificationType?.toLowerCase() === "reminderterm";

export const isInvestmentQuestionnaireDestination = (
	type: string | undefined,
	notificationType: string | undefined,
): boolean =>
	normalizeNavigationDestination(type) ===
		NavigationDestinationType.investmentQuestionnaire ||
	notificationType?.toLowerCase() === "investmentquestionnaire";

export const isWebDestination = (type: string | undefined): boolean =>
	normalizeNavigationDestination(type) !== undefined;

export const getDestinationRoute = (type: string | undefined) => {
	const destination = normalizeNavigationDestination(type);
	return destination ? DESTINATION_ROUTES[destination] : undefined;
};

const logNavigationIssue = (reason: string, payload: NavigationPayload) =>
	logWarning(reason, {
		notification_type: payload.type,
		notification_id: payload.id,
		notification_source_id: payload.sourceId,
	});

export const fallbackToNotificationList = (
	reason: string,
	payload: NavigationPayload,
	navigate: (path: string) => void,
) => {
	logNavigationIssue(reason, payload);
	toast.error(i18n.t(Resources.Common.somethingWentWrong));
	navigate(AppRouting.MyPlann.Notifications.path());
};

export const navigateToSource = (
	route: (sourceId: string) => string,
	payload: NavigationPayload,
	navigate: (path: string) => void,
) => {
	if (!payload.sourceId) {
		fallbackToNotificationList(
			"Notification navigation without sourceId",
			payload,
			navigate,
		);
		return;
	}
	navigate(route(payload.sourceId));
};
